import React from 'react';
import Text from 'src/@vymo/ui/atoms/text';
import { Keys, locale } from 'src/i18n';
import {
  BULK_ALLOCATION,
  BULK_REVOCATION,
  CRM_UPDATE_STATUS,
  DDP_DETAILS,
  WORKFLOW_DDP_STATUS,
} from 'src/modules/constants';
import { renderModule } from 'src/workspace/slice';
import { RowClickParams } from './types';
import styles from './index.module.scss';

const CRM_UPDATE_STATUS_FIXED_WIDTH_CODES = [
  'response',
  'request',
  'errorMessage',
  'headers',
];

export const formatReadableDate = (value) => {
  if (!value) {
    return '-';
  }

  const d = new Date(value);

  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();

  const time = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${day} ${month}, ${year} at ${time}`;
};

export const handleRowClick = async ({
  dispatch,
  module,
  rowId,
  rowData,
}: RowClickParams) => {
  switch (module) {
    case WORKFLOW_DDP_STATUS: {
      const jobId = rowData?.jobId ?? rowId;
      if (!jobId) return;
      dispatch(
        renderModule({
          id: DDP_DETAILS,
          props: { jobId },
        }),
      );
      break;
    }

    case BULK_ALLOCATION: {
      const jobId = rowData?.jobId ?? rowId;
      if (!jobId) return;
      dispatch(
        renderModule({
          id: DDP_DETAILS,
          props: { jobId, actionType: 'bulkUpload' },
        }),
      );
      break;
    }

    case BULK_REVOCATION: {
      const jobId = rowData?.jobId ?? rowId;
      if (!jobId) return;
      dispatch(
        renderModule({
          id: DDP_DETAILS,
          props: { jobId, actionType: 'bulkRevoke' },
        }),
      );
      break;
    }

    default:
      break;
  }
};

export const getGenericListDisplayName = (module: string): string => {
  switch (module) {
    case WORKFLOW_DDP_STATUS:
    case BULK_ALLOCATION:
    case BULK_REVOCATION:
      return locale(Keys.WORKFLOW_DOWNLOADS);

    case CRM_UPDATE_STATUS:
      return locale(Keys.CRM_UPDATE_STATUS);

    default:
      return '';
  }
};

export const mapCrmUpdateStatusColumns = (columns: any[] = []) =>
  columns
    .filter((column) => column?.visible?.enabledFor?.includes('web'))
    .map((column: any) => {
      if (!CRM_UPDATE_STATUS_FIXED_WIDTH_CODES.includes(column?.code)) {
        return column;
      }

      return {
        ...column,
        cell: (value) => (
          <div className={styles.genericList__fixedWidthCell}>
            <Text type="description">{value ?? '-'}</Text>
          </div>
        ),
      };
    });

const getFieldType = (type: string) => {
  switch (type) {
    case 'spinner':
      return 'code_name_spinner';
    default:
      return type;
  }
};

// Transform columns to Form input field format
export const transformColumnsToFilterFields = (columns: any[]) => {
  if (!columns || !Array.isArray(columns)) {
    return [];
  }

  return columns
    .filter((column) => (column?.filter?.enabledFor ?? []).includes('web'))
    .map((column) => {
      if (column.input) {
        return column.input;
      }
      const fieldCode = column.field || column.code;
      const fieldType = getFieldType(column.type || 'text');

      const inputConfig: any = {
        code: fieldCode,
        type: fieldType,
        hint: column.title || column.name || '',
        required: false,
        placeholder: null,
        invisible: false,
        hidden: false,
        read_only: false,
      };

      if (column.default_value !== undefined && column.default_value !== null) {
        inputConfig.default_value = column.default_value;
      }

      // Handle spinner type - convert filters array to options with code and name
      if (column.filters && Array.isArray(column.filters)) {
        inputConfig.options = column.filters.map((filter: any) => {
          if (typeof filter === 'object' && filter !== null) {
            return {
              code: filter.code || filter.value || String(filter),
              name:
                filter.name ||
                filter.label ||
                filter.code ||
                filter.value ||
                String(filter),
            };
          }
          return {
            code: String(filter),
            name: String(filter),
          };
        });
      }

      return inputConfig;
    });
};
